import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

const publicProductCache = new Map();

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.updatePublicRetailPrice();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
      this.updatePublicRetailPrice();
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  updatePublicRetailPrice = async () => {

    const retailPrice = /** @type {HTMLElement|null} */ (this.querySelector('[data-public-retail-price]'));
    if (!retailPrice) return;

    const handle = retailPrice.dataset.productHandle;
    const variantId = Number(retailPrice.dataset.variantId);
    const currentPrice = Number(retailPrice.dataset.currentPrice);
    const moneyFormat = retailPrice.dataset.moneyFormat || '${{amount}}';
    const currency = retailPrice.dataset.currency || 'AUD';
    const label = retailPrice.querySelector('[data-public-retail-price-text]');

    if (!handle || !variantId || !Number.isFinite(currentPrice) || !label) return;

    try {

      const publicProduct = await getPublicProduct(handle);
      const publicVariant = publicProduct?.variants?.find((variant) => Number(variant.id) === variantId);
      const publicPrice = Number(publicVariant?.price);
      const publicPriceIncludingGst = Math.round(publicPrice * 1.1);

      if (!Number.isFinite(publicPriceIncludingGst) || publicPriceIncludingGst <= currentPrice) {
        retailPrice.hidden = true;
        label.textContent = '';
        return;
      }

      label.textContent = `Recommended retail price ${formatMoney(publicPriceIncludingGst, moneyFormat, currency)}`;
      retailPrice.hidden = false;
    } catch (error) {
      retailPrice.hidden = label.textContent.trim() === '';
      console.warn('Unable to load public retail price', error);
      label.textContent = `Recommended retail price: Unavailable (dev mode)`
      retailPrice.hidden = false;
    }
  };
}

async function getPublicProduct(handle) {
  if (!publicProductCache.has(handle)) {
    publicProductCache.set(
      handle,
      fetch(`/products/${encodeURIComponent(handle)}.js`, {
        cache: 'no-store',
        credentials: 'omit',
      }).then((response) => {
        if (!response.ok) throw new Error(`Product JSON request failed: ${response.status}`);
        return response.json();
      })
    );
  }

  return publicProductCache.get(handle);
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
